import { Link } from "react-router-dom";
import { SEOHead } from "@/components/SEOHead";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { BlogBreadcrumb } from "@/components/seo/BlogBreadcrumb";
import { RelatedReads } from "@/components/seo/RelatedReads";
import { getRelatedPosts } from "@/content/blogPosts";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { CalendarDays, Snowflake } from "lucide-react";
import heroImage from "@/assets/gallery/friends-tubing.jpg";
import inlineImage from "@/assets/gallery/river-canopies.jpg";

const SLUG = "lazy-river-near-me-guadalupe";
const CANONICAL = `/blog/${SLUG}`;
const TITLE =
  'Lazy River Near Me? Float the Guadalupe | Son\'s Guadalupe';
const DESCRIPTION =
  "Searching 'lazy river near me'? Skip the concrete loops — float the spring-fed Guadalupe River with shuttle service and private riverside cabanas.";
const PUBLISH_DATE = "July 23, 2026";
const HERO_ALT =
  "Friends drifting shoulder-to-shoulder in bright tubes down the cypress-shaded Guadalupe River near New Braunfels, Texas.";

const FAQS: { q: string; a: string }[] = [
  {
    q: "Where can I find a lazy river near me in the New Braunfels area?",
    a: "If you are looking for a lazy river near you in New Braunfels, you can choose between artificial waterparks or a natural float. Schlitterbahn New Braunfels offers man-made lazy rivers, while Son's Guadalupe on the Guadalupe River provides a wide, natural, spring-fed lazy river experience under a canopy of cypress trees.",
  },
  {
    q: "What is the difference between a waterpark lazy river and natural river tubing?",
    a: "Waterpark lazy rivers feature chlorinated, recirculated tap water, mechanical currents, and crowded loops surrounded by concrete. Natural river tubing on the Guadalupe River features spring-fed 72-degree water, natural currents, limestone cliffs, and wide-open lanes to spread out with your family.",
  },
  {
    q: "How cold is the water on the Guadalupe River natural float?",
    a: "The Guadalupe River is fed by cold, spring-fed waters that stay at a crisp 72 degrees, making it a perfect escape even during the middle of a brutal July heatwave.",
  },
  {
    q: "Is tubing at Schlitterbahn New Braunfels relaxing?",
    a: "While Schlitterbahn is world-famous for high-energy thrills and water coasters, its lazy rivers tend to feel like a packed amusement park. On hot weekends, you will likely float shoulder-to-shoulder in slow-moving, chlorinated bottlenecks with screaming crowds and loud speakers, which is not ideal if you want to unplug.",
  },
  {
    q: "How do logistics work for a natural river float at Son's Guadalupe?",
    a: "We handle all the details so you can relax. You park once in our secure, private lot, and our air-conditioned shuttle takes you upriver to the launch point. You enjoy a scenic 2-to-3 hour float down the Guadalupe, which brings you directly back to your private cabana.",
  },
  {
    q: "Do I have to wait for a shuttle ride in a hot parking lot when my float ends?",
    a: "No. Unlike public exit points where you must haul heavy tubes up mud banks and wait in the heat for a bus, our float is timed so you exit the river directly at your own private cabana. Your dry clothes, cold drinks, and grill are waiting right there.",
  },
  {
    q: "What makes Son's Guadalupe different from traditional public float options?",
    a: "Traditional public options like Texas Tubes or the Comal River draw massive, high-energy crowds, leaving you to float shoulder-to-shoulder with thousands of day-trippers. Son's Guadalupe caps guest capacity for a calmer experience and allows you to reserve private riverside cabanas with comfortable seating, BBQ grills, and electricity.",
  },
  {
    q: "Are there private cabanas available to rent at Son's Guadalupe?",
    a: "Yes. Son's Guadalupe offers private riverside cabanas that you can reserve. These shaded shelters come equipped with comfortable seating, a BBQ grill, electricity, and direct access to your grassy bank where your river float ends.",
  },
];

const TOC = [
  { id: "elements", label: "Elements of a Perfect Lazy River" },
  { id: "comparison", label: "Waterpark vs. Natural River" },
  { id: "process", label: "The Stress-Free Float Process" },
  { id: "river-style", label: "Choosing Your River Style" },
  { id: "safety", label: "Safety & Water Conditions" },
  { id: "planning", label: "Packing List & Travel Times" },
  { id: "faqs", label: "FAQs" },
];

const schemaJson = [
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://sonsguadalupe.com/" },
      { "@type": "ListItem", position: 2, name: "Blog", item: "https://sonsguadalupe.com/blog" },
      {
        "@type": "ListItem",
        position: 3,
        name: "Lazy River Near Me — Guadalupe River",
        item: `https://sonsguadalupe.com${CANONICAL}`,
      },
    ],
  },
  {
    "@context": "https://schema.org",
    "@type": "Article",
    headline:
      'Looking for a "Lazy River Near Me"? Why the Guadalupe River is the Ultimate Natural Float',
    description:
      "Why the Guadalupe River offers a real lazy-river float with spring-fed water, limestone cliffs, and more space than resort pools.",
    author: { "@type": "Organization", name: "Son's Guadalupe" },
    publisher: {
      "@type": "Organization",
      name: "Son's Guadalupe",
      logo: {
        "@type": "ImageObject",
        url: "https://sonsguadalupe.com/og-image.png",
      },
    },
    datePublished: "2026-07-23",
    mainEntityOfPage: `https://sonsguadalupe.com${CANONICAL}`,
    url: `https://sonsguadalupe.com${CANONICAL}`,
    wordCount: 1450,
  },
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQS.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  },
];

const BlogLazyRiverNearMe = () => {
  const related = getRelatedPosts(SLUG, 3);

  return (
    <div className="min-h-screen">
      <SEOHead
        title={TITLE}
        description={DESCRIPTION}
        canonical={CANONICAL}
        ogImage={heroImage}
        schemaJson={schemaJson}
      />
      <Header />

      <main>
        <article className="container-narrow mx-auto px-4 md:px-8 pt-28 pb-16">
          <BlogBreadcrumb title='Lazy River Near Me — Guadalupe River' />

          <header className="mb-8">
            <p className="text-xs uppercase tracking-[0.2em] text-teal font-semibold mb-3">
              Guide
            </p>
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-charcoal mb-4">
              Looking for a "Lazy River Near Me"? Why the Guadalupe River is the Ultimate Natural Float
            </h1>
            <p className="flex items-center gap-2 text-sm text-charcoal/60 mb-6">
              <CalendarDays className="w-4 h-4" /> Published {PUBLISH_DATE} · 7 min read
            </p>
            <div className="flex flex-wrap gap-3">
              <Link to="/book-now">
                <Button variant="hero" size="lg">Book Now</Button>
              </Link>
              <Link to="/cabanas">
                <Button variant="outline" size="lg">Reserve a Riverside Cabana</Button>
              </Link>
            </div>
          </header>

          <img
            src={heroImage}
            alt={HERO_ALT}
            width={1600}
            height={900}
            loading="eager"
            decoding="async"
            className="w-full h-auto rounded-2xl shadow-soft mb-10"
          />

          <aside className="rounded-2xl border-l-4 border-teal bg-muted/40 p-6 mb-10">
            <p className="text-xs uppercase tracking-[0.18em] text-teal font-semibold mb-2">
              Quick Answer
            </p>
            <p className="text-charcoal/85 leading-relaxed">
              The best natural lazy river near New Braunfels is the spring-fed{" "}
              <strong>Guadalupe River</strong>. Instead of chlorinated concrete loops, you get 72°F water,
              cypress shade, and wide-open lanes. At{" "}
              <Link to="/why-sons" className="text-teal underline font-medium">Son's Guadalupe</Link>{" "}
              you park once, ride an air-conditioned shuttle upriver, and float straight back to your
              own <Link to="/cabanas" className="text-teal underline font-medium">private riverside cabana</Link>.
            </p>
          </aside>

          <nav aria-label="Table of contents" className="rounded-xl border border-border bg-card/60 p-5 mb-10">
            <p className="font-heading text-sm font-bold text-charcoal mb-3">On this page</p>
            <ul className="grid sm:grid-cols-2 gap-y-2 gap-x-6 text-sm">
              {TOC.map((t) => (
                <li key={t.id}>
                  <a href={`#${t.id}`} className="text-teal hover:underline">
                    → {t.label}
                  </a>
                </li>
              ))}
            </ul>
          </nav>

          <div className="prose prose-lg max-w-none text-charcoal/85">
            <p className="text-xl leading-relaxed">
              When summer bakes Texas, finding cool water becomes an absolute necessity. Type{" "}
              <em>"lazy river near me"</em> into your phone, and you will mostly get pictures of blue-painted
              concrete, smelling of heavy chlorine and packed into crowded resort pools. Those man-made loops
              are fine for some, but they cannot compete with the real thing. Our spring-fed channels winding
              through the Texas Hill Country offer actual elbow room, ancient limestone cliffs, and true peace.
            </p>
            <p>
              The Guadalupe River cuts right through New Braunfels, serving as the ultimate natural lazy river
              in the state. We trade the artificial currents and plastic dividers for a wide, scenic float
              beneath a massive canopy of cypress trees. Even so, trading a controlled resort pool for a wild
              river can feel a little daunting if you are trying to figure out the logistics on your own.
            </p>
            <p>
              By letting our team handle your shuttles, safety, and comfort, you get the effortless relaxation
              of a lazy river without the theme-park headaches. If you are still weighing your options, our
              guide to{" "}
              <Link to="/private-vs-public-guadalupe" className="text-teal underline">
                private vs. public river spots
              </Link>{" "}
              breaks down exactly what changes when you skip the public access points.
            </p>

            <h2 id="elements" className="font-heading text-3xl font-bold text-charcoal mt-12 mb-4">
              The Elements of a Perfect Lazy River Escape
            </h2>
            <p>
              When you start planning a day on the water, you are usually chasing three simple things: cold
              water, zero effort, and a real break from the daily grind.
            </p>
            <p>
              Chlorinated resort loops give you a controlled environment. They are predictable, paved, and
              require absolutely no navigation. But for families and couples who actually want to unwind, that
              resort magic wears off fast. Between the loud pool filters, the chemical smell, and the feeling
              of drifting in circles with hundreds of strangers, it starts to feel like an amusement park line
              instead of a getaway.
            </p>
            <p>
              Natural rivers are different. The Guadalupe River flows at the pace of the Texas Hill Country,
              fed by cold, spring-fed waters that stay a crisp 72 degrees even in the middle of a brutal July
              heatwave. You trade concrete walls for limestone bluffs, green river bends, and wide-open blue
              skies. Best of all, you actually go somewhere. Instead of being stuck in a loop, you drift from
              one beautiful spot to another.
            </p>
            <p>
              It comes down to what kind of break you actually need: a repetitive, chemical-heavy pool, or a
              real, open-air river journey.
            </p>

            <h2 id="comparison" className="font-heading text-3xl font-bold text-charcoal mt-12 mb-4">
              Comparing Artificial Waterparks vs. Natural River Tubing
            </h2>
            <p>
              To help you decide which environment fits your planned day on the water, it helps to look at the
              practical differences side-by-side.
            </p>

            <div className="not-prose my-6 overflow-x-auto rounded-xl border border-border">
              <table className="w-full text-sm">
                <thead className="bg-muted/60 text-charcoal">
                  <tr>
                    <th className="text-left p-3 font-heading">Feature</th>
                    <th className="text-left p-3 font-heading">Artificial Waterpark Lazy Rivers</th>
                    <th className="text-left p-3 font-heading">Natural River Tubing (Guadalupe)</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border text-charcoal/85">
                  <tr>
                    <td className="p-3 font-semibold">Water Quality</td>
                    <td className="p-3">Chlorinated, recirculated tap water</td>
                    <td className="p-3">Spring-fed, natural flowing river water</td>
                  </tr>
                  <tr>
                    <td className="p-3 font-semibold">Scenery</td>
                    <td className="p-3">Concrete pathways, waterslides, buildings</td>
                    <td className="p-3">Limestone cliffs, bald cypress, natural wildlife</td>
                  </tr>
                  <tr>
                    <td className="p-3 font-semibold">Space & Crowds</td>
                    <td className="p-3">Bumper-to-bumper tubes in a narrow lane</td>
                    <td className="p-3">Wide, open river lanes with room to spread out</td>
                  </tr>
                  <tr>
                    <td className="p-3 font-semibold">Flow & Movement</td>
                    <td className="p-3">Fixed mechanical current, continuous loop</td>
                    <td className="p-3">Natural currents, real downstream progress</td>
                  </tr>
                  <tr>
                    <td className="p-3 font-semibold">Atmosphere</td>
                    <td className="p-3">Loud music, whistle-blowing lifeguards</td>
                    <td className="p-3">Peaceful, natural sounds, relaxed pace</td>
                  </tr>
                  <tr>
                    <td className="p-3 font-semibold">Basecamp</td>
                    <td className="p-3">First-come deck chairs or pricey cabanas</td>
                    <td className="p-3">Private riverside cabanas & shaded banks</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <h3 className="font-heading text-2xl font-bold text-charcoal mt-8 mb-3">
              Inside the Waterpark
            </h3>
            <p>
              Schlitterbahn New Braunfels is a world-famous park built for high-energy thrills. If your
              teenagers want to sprint between intense water coasters, massive wave pools, and chaotic play
              zones, it is a solid choice.
            </p>
            <p>
              Just keep in mind that the lazy rivers here feel exactly like a packed amusement park. On hot
              summer weekends, you will likely float shoulder-to-shoulder in a slow-moving, chlorinated
              bottleneck of plastic tubes. Between the screaming crowds, whistling lifeguards, and blasting
              speakers, it is tough to find a single moment of actual peace if your goal is to unplug and
              decompress.
            </p>

            <h3 className="font-heading text-2xl font-bold text-charcoal mt-8 mb-3">
              Out on the Real River
            </h3>
            <p>
              The Guadalupe River feels like another world entirely. Floating here is all about slowing down.
              Because the riverbed is so wide, your group can actually spread out and drift together without
              bumping into strangers every three seconds.
            </p>
            <p>
              Gravity and the natural currents of the Texas Hill Country do all the work, gently guiding you
              down the river bend while you kick back. Massive, historic bald cypress trees line the banks to
              provide thick pockets of natural shade, catching cool breezes you simply cannot replicate inside
              a concrete park. It is a genuine Texas tradition that lets you relax and enjoy the scenery at
              your own pace — and if you want the full walkthrough of the float itself, our{" "}
              <Link to="/tubing-guadalupe-river-new-braunfels" className="text-teal underline">
                tubing the Guadalupe River
              </Link>{" "}
              guide covers everything from put-in to takeout.
            </p>

            <img
              src={inlineImage}
              alt="Cypress canopies arching over a calm bend of the Guadalupe River near Son's Guadalupe."
              width={1600}
              height={900}
              loading="lazy"
              decoding="async"
              className="w-full h-auto rounded-2xl shadow-soft my-10"
            />

            <h2 id="process" className="font-heading text-3xl font-bold text-charcoal mt-12 mb-4">
              A Streamlined, Stress-Free Floating Process
            </h2>
            <p>
              A natural river is not a swimming pool. If you have never been, figuring out the logistics can
              feel like a chore. People worry about where they will park, how they will get back to their
              vehicles, and whether they will miss the exit.
            </p>
            <p>
              A professional outfitter handles those details. We take the guesswork out of the day so you can
              focus on the water.
            </p>

            <pre className="bg-charcoal/5 text-charcoal/80 rounded-lg p-4 overflow-x-auto text-sm leading-6">
{`[Step 1: Park & Prep]   Onsite parking, change rooms, prep coolers at basecamp.
[Step 2: Shuttle Ride]  Air-conditioned shuttle transports your group upriver.
[Step 3: The Float]     Enjoy a peaceful 2-to-3 hour scenic float down the Guadalupe.
[Step 4: The Arrival]   Exit the river directly at your private cabana — no long walks.`}
            </pre>

            <h3 className="font-heading text-2xl font-bold text-charcoal mt-8 mb-3">
              The Magic of Shuttle-Assisted Tubing
            </h3>
            <p>
              The shuttle makes the whole trip work. You do not need to bring two cars and stage them at
              different bridges down the road. Instead, you park once in a secure, private lot.
            </p>
            <p>
              We take care of the driving. Our cool, air-conditioned shuttle takes your family and your gear
              upriver to our launch spot. You plop your tube in the water and let the current do the work.
              There is no paddling required. We timed the route perfectly so the current brings you right
              back to where you started, meaning you never have to guess when to get out.
            </p>

            <h3 className="font-heading text-2xl font-bold text-charcoal mt-8 mb-3">
              Why Landing Back at a Private Cabana Beats the Public Scramble
            </h3>
            <p>
              Public exit points are usually chaotic. You have to haul heavy, wet tubes up slippery mud banks,
              wait in baking heat for a bus, and trek across giant gravel parking lots in flip-flops.
            </p>
            <p>
              Our setup avoids that headache entirely. Your float ends right at your own shaded shelter. You
              just step out of the Guadalupe and onto your private lawn. Your dry clothes, cold drinks, grill,
              and outlets are waiting right there. No damp car rides or long lines stand between you and a
              hot burger. Reserve your basecamp on the{" "}
              <Link to="/cabanas" className="text-teal underline">private riverside cabana</Link>{" "}
              page.
            </p>

            <h2 id="river-style" className="font-heading text-3xl font-bold text-charcoal mt-12 mb-4">
              Choosing Your River Style in New Braunfels
            </h2>
            <p>
              Planning a trip to our local rivers means choosing between a few very different setups. The
              right fit comes down to how much elbow room, comfort, and peace you actually want.
            </p>

            <h3 className="font-heading text-2xl font-bold text-charcoal mt-8 mb-3">
              Classic Public Floating with Big Crowds
            </h3>
            <p>
              If you want a high-energy party with wall-to-wall people, the traditional public outfitters have
              you covered.
            </p>
            <ul>
              <li>
                <strong>Texas Tubes</strong> sits on the Comal River. It is a high-volume operation that draws
                massive college crowds and weekend travelers looking for a loud, social scene. Because these
                public entry points are open to everyone, the river gets packed tight during the summer,
                leaving you with nowhere to escape the heat once your float wraps up.
              </li>
              <li>
                <strong>The Float In</strong> also operates on the Comal, offering a park setup with decks and
                river access. While the property is nice, the Comal is a short, highly congested river. You
                will be floating shoulder-to-shoulder with thousands of day-trippers.
              </li>
            </ul>

            <h3 className="font-heading text-2xl font-bold text-charcoal mt-8 mb-3">
              Quiet and Easy at Son's Guadalupe
            </h3>
            <p>
              If you are bringing the kids, planning a family reunion, or just want to skip the chaotic public
              parks, <strong>Son's Guadalupe</strong> offers a much calmer way to experience the river.
            </p>
            <p>
              We capped our guest capacity. By limiting the crowd size, we keep our grounds, parking lot, and
              waterfront spacious and quiet. You do not have to fight for a patch of grass here because you
              can reserve your own private riverside cabana complete with comfortable seating, a BBQ grill,
              and electricity.
            </p>
            <p>
              Our shuttle setup is incredibly simple. We drive you upriver, you enjoy a beautiful
              two-to-three-hour float, and you exit the water right here at our property. You walk straight
              out of the river and back to your private lounge chair. With clean restrooms, hot showers, and
              our team on-site to help, you get all the comforts of a resort right on the shady banks of the
              Guadalupe. Ready to lock in a date? Head to{" "}
              <Link to="/book-now" className="text-teal underline font-medium">book now</Link>.
            </p>

            <h2 id="safety" className="font-heading text-3xl font-bold text-charcoal mt-12 mb-4">
              Safety, Water Conditions, and Pacing on the Guadalupe River
            </h2>
            <p>
              Keeping everybody safe is your top priority. We get it. It is completely normal to wonder how a
              wild Texas river compares to the concrete, chlorinated walls of a local swimming pool.
            </p>
            <p>
              Our stretch of the Guadalupe River is spring-fed. That means the water stays clean, clear, and a
              crisp 72°F all summer long. Honestly, nothing beats that icy shock when the Texas heat gets
              heavy.
            </p>

            <aside className="not-prose rounded-2xl border-l-4 border-teal bg-teal/5 p-6 my-6 flex gap-4">
              <Snowflake className="w-8 h-8 text-teal shrink-0" />
              <div>
                <p className="font-heading font-bold text-charcoal mb-1">
                  Guadalupe River Water Temperature: 72°F (22°C) year-round
                </p>
                <p className="text-sm text-charcoal/75">
                  Source: Spring-fed natural underground aquifers.
                </p>
              </div>
            </aside>

            <p>
              Public pools need a chemistry set of heavy chlorine to stay clean, but the Guadalupe relies on a
              constant, natural underground flow to keep things fresh. The river paces itself beautifully
              here. It is mostly just a slow, lazy crawl, especially along the wide, shallow bends where we
              launch our tubes.
            </p>
            <p>Keeping your crew safe is simple:</p>
            <ul>
              <li>
                <strong>Life Jackets:</strong> Grab a high-quality vest from us. We keep plenty on hand for
                kids and anyone who is not a strong swimmer.
              </li>
              <li>
                <strong>Tube Tethering:</strong> Tie your tubes together. This is the easiest way to keep the
                kids close and ensure nobody drifts off-course during the float.
              </li>
              <li>
                <strong>Professional Monitoring:</strong> We check the river flow rates and daily water levels
                constantly, meaning we will not put you on the water unless conditions are absolutely perfect
                for a relaxed family float.
              </li>
            </ul>

            <h2 id="planning" className="font-heading text-3xl font-bold text-charcoal mt-12 mb-4">
              Planning Your Day Trip: Packing Checklist and Travel Times
            </h2>
            <p>
              A little advance planning keeps your river day simple and fun. Since you are hanging out in a
              real, living river environment, bringing the right gear means nobody is complaining about
              sunburn or sore feet halfway through the afternoon.
            </p>

            <h3 className="font-heading text-2xl font-bold text-charcoal mt-8 mb-3">
              Essential Packing Checklist
            </h3>
            <ul>
              <li>
                <strong>Footwear:</strong> Water shoes or sandals with heel straps. The riverbed is full of
                natural limestone rocks, so flip-flops will just float away the second you step in.
              </li>
              <li>
                <strong>Sun Protection:</strong> Sunscreen, polarized sunglasses with a floating strap, and a
                good hat.
              </li>
              <li>
                <strong>Cooler & Refreshments:</strong> A sturdy cooler loaded with water, sports drinks, and
                lunch. Double-check the local river container laws before you pack up, and please use reusable
                containers so we can keep our stretch of the water pristine.
              </li>
              <li>
                <strong>Dry Bag:</strong> Toss your car keys, phones, and dry towels into a small waterproof
                bag to keep them safe while you float.
              </li>
            </ul>

            <h3 className="font-heading text-2xl font-bold text-charcoal mt-8 mb-3">
              Travel Times to New Braunfels
            </h3>
            <p>
              We are tucked right into the Texas Hill Country, making us an easy drive for a quick Saturday
              escape or a full weekend stay:
            </p>
            <ul>
              <li><strong>From San Antonio:</strong> About 40 minutes driving north on I-35.</li>
              <li><strong>From Austin:</strong> Around 50 minutes heading south down I-35.</li>
              <li><strong>From Houston:</strong> A straightforward 2.5-hour drive west on I-10.</li>
            </ul>
            <p>
              Skip the crowded, concrete water parks. Choosing a natural float with our easy shuttle service
              lets you experience the real Texas outdoors without the typical logistical headaches. When you
              spend the afternoon drifting under massive, shady cypress trees and walk straight back to your
              own private cabana, you will realize a resort pool cannot compete with this.
            </p>

            <h2 id="faqs" className="font-heading text-3xl font-bold text-charcoal mt-12 mb-4">
              Frequently Asked Questions
            </h2>
            <Accordion type="single" collapsible className="not-prose">
              {FAQS.map((f, i) => (
                <AccordionItem key={i} value={`faq-${i}`}>
                  <AccordionTrigger className="text-left font-heading text-lg">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-charcoal/80 leading-relaxed">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>

            <div className="not-prose mt-12 rounded-2xl bg-teal/10 border border-teal/30 p-8 text-center">
              <h3 className="font-heading text-2xl font-bold text-charcoal mb-3">
                Ready to Float the Real Thing?
              </h3>
              <p className="text-charcoal/80 mb-5">
                Reserve a private riverside cabana, add shuttle-assisted tubing, and land right back at your
                basecamp.
              </p>
              <div className="flex flex-wrap gap-3 justify-center">
                <Link to="/book-now">
                  <Button variant="hero" size="lg">Book Now</Button>
                </Link>
                <Link to="/cabanas">
                  <Button variant="outline" size="lg">See Cabanas</Button>
                </Link>
              </div>
            </div>
          </div>
        </article>

        <RelatedReads posts={related} />
      </main>

      <Footer />
    </div>
  );
};

export default BlogLazyRiverNearMe;
